/**
 * F17 T17.1.5 — auto-layout grafu przez dagre (top-bottom).
 *
 * Używane przy:
 *  - imporcie/loadowaniu procesu który nie ma pozycji (legacy bez pozycji),
 *  - przycisku "Auto-layout" w buildera (manual reflow po większych zmianach).
 *
 * Wymiary węzłów: dagre potrzebuje width/height żeby uniknąć kolizji. Gdy węzeł został już
 * narysowany, bierzemy jego REALNY rozmiar zmierzony przez React Flow (`node.measured` — stan
 * kreatora go niesie, bo przechodzi przez `applyNodeChanges`); inaczej szacunek per nodeType.
 * Rozmiar z layoutu musi się zgadzać z narysowanym, bo pozycję węzła (lewy górny róg) liczymy
 * jako środek z dagre − pół rozmiaru: przy rozjeździe (np. pigułka etapu końcowego jest
 * węższa niż szacunek) węzeł ląduje obok osi kolumny, a strzałka do jego górnego środka biegnie
 * po skosie.
 *
 * Diagram sprawy (`ProcessGraphViewer`) układa graf PRZED pierwszym pomiarem, więc nie ma
 * rozmiarów; tam używamy `anchor: 'center'` — pozycja to środek węzła, a React Flow stawia go
 * środkiem w tym punkcie bez względu na rzeczywistą szerokość.
 *
 * Algorithm: dagre `rankdir: TB`, `nodesep: 60`, `ranksep: 80`. Te wartości
 * dają komfortową przestrzeń + sensowny dla procesu z 5-15 węzłami.
 */

import dagre from '@dagrejs/dagre';
import type { Edge, Node } from '@xyflow/react';

import type { ProcessEdgeData, ProcessNodeData } from './graphTypes';

const NODE_SIZE: Record<string, { width: number; height: number }> = {
  start: { width: 140, height: 60 },
  // Etap końcowy to pigułka z nazwą (`EndNode`): szerokość do `max-w-[220px]`, wysokość
  // = tekst `text-sm` (20 px) + `py-2.5` (2 × 10 px) = 40 px. Bierzemy wartości maksymalne, żeby
  // pigułki z długą nazwą nie nachodziły na sąsiadów.
  end: { width: 220, height: 40 },
  parallel_split: { width: 160, height: 60 },
  parallel_join: { width: 160, height: 60 },
  decision: { width: 220, height: 110 },
};

const DEFAULT_SIZE = { width: 180, height: 80 };

/** Punkt zaczepienia węzła w jego środku — dla `anchor: 'center'` (patrz `AutoLayoutOptions`). */
const CENTER_ORIGIN: NonNullable<Node['origin']> = [0.5, 0.5];

export interface AutoLayoutOptions {
  /**
   * Co oznacza `position` w zwracanych węzłach:
   *  - `'top-left'` (domyślnie) — lewy górny róg węzła, jak pozycje zapisywane w grafie kreatora;
   *  - `'center'` — ŚRODEK węzła; węzeł dostaje `origin: [0.5, 0.5]`, więc React Flow stawia go
   *    środkiem w tym punkcie niezależnie od jego rzeczywistej (jeszcze niezmierzonej) szerokości.
   *    Tylko dla układów NIEZAPISYWANYCH (np. diagram sprawy) — zapis w grafie zakłada lewy górny róg.
   */
  anchor?: 'top-left' | 'center';
  /** Kierunek layoutu: `TB` (top-bottom, domyślny) lub `LR` (left-right). */
  direction?: 'TB' | 'LR';
  /** Odległość między węzłami w tym samym poziomie. */
  nodesep?: number;
  /** Odległość między poziomami. */
  ranksep?: number;
  /**
   * Rozmiar węzła użyty gdy jego `type` nie jest w `NODE_SIZE` — override dla grafów
   * spoza Procesów (np. organigram, gdzie kafelek pracownika ma inne wymiary niż węzły
   * obiegu). Domyślnie `DEFAULT_SIZE` (180×80) — zachowanie dla Procesów bez zmian.
   */
  nodeSize?: { width: number; height: number };
}

/**
 * Oblicza nowe pozycje wszystkich węzłów wg layoutu dagre.
 *
 * Generyczne po typie danych węzła/krawędzi — działa dla grafu Procesów (domyślnie)
 * oraz dowolnego innego (np. organigram). Zwraca *nowe* obiekty Node — nie mutuje
 * wejścia. Krawędzie nie są zmieniane (Reactflow renderuje je deterministycznie
 * z source/target pozycji).
 */
export function autoLayout<
  NData extends Record<string, unknown> = ProcessNodeData,
  EData extends Record<string, unknown> = ProcessEdgeData,
>(nodes: Node<NData>[], edges: Edge<EData>[], options: AutoLayoutOptions = {}): Node<NData>[] {
  const { direction = 'TB', nodesep = 60, ranksep = 80, nodeSize, anchor = 'top-left' } = options;
  const fallbackSize = nodeSize ?? DEFAULT_SIZE;

  if (nodes.length === 0) return nodes;

  /** Rozmiar węzła w layoucie: zmierzony przez React Flow (gdy jest), inaczej szacunek per typ. */
  const sizeOf = (node: Node<NData>): { width: number; height: number } => {
    const { width, height } = node.measured ?? {};
    if (width !== undefined && height !== undefined && width > 0 && height > 0) {
      return { width, height };
    }
    return node.type ? (NODE_SIZE[node.type] ?? fallbackSize) : fallbackSize;
  };

  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: direction, nodesep, ranksep });

  for (const node of nodes) {
    const size = sizeOf(node);
    g.setNode(node.id, { width: size.width, height: size.height });
  }
  for (const edge of edges) {
    g.setEdge(edge.source, edge.target);
  }

  dagre.layout(g);

  return nodes.map((node) => {
    const laid = g.node(node.id);
    // dagre.node() zwraca union '' | NodeData — guard na obie ścieżki.
    if (!laid || typeof laid === 'string') return node;
    // Dagre zwraca CENTER pozycji. `anchor: 'center'` oddaje go wprost (węzeł z origin w środku).
    if (anchor === 'center') {
      return { ...node, origin: CENTER_ORIGIN, position: { x: laid.x, y: laid.y } };
    }
    // Reactflow oczekuje TOP-LEFT — przesuwamy o połowę wymiarów node'a (tych samych, które
    // dostał dagre).
    const size = sizeOf(node);
    return {
      ...node,
      position: {
        x: laid.x - size.width / 2,
        y: laid.y - size.height / 2,
      },
    };
  });
}
